import type {
  InsightAxis,
  InsightAxisSources,
  InsightPayload,
  InsightSourceRefs,
  ProcessedInsightAxis,
} from '@/types/insights';

const toSourceMap = (refs: InsightSourceRefs[]) =>
  new Map(refs.map(ref => [ref.id, ref.sources]));

const mergeAxis = (
  axis: ProcessedInsightAxis,
  latest: InsightAxisSources,
): ProcessedInsightAxis => {
  const topicSources = toSourceMap(latest.topics);
  const reasonSources = toSourceMap(latest.hypothesis_reasons);

  return {
    ...axis,
    source_generation_status: latest.source_generation_status,
    sources: latest.sources,
    topics: axis.topics.map(topic => ({
      ...topic,
      sources: topicSources.get(topic.id) ?? topic.sources,
    })),
    hints: axis.hints.map(hint => ({
      ...hint,
      topic:
        hint.topic === null
          ? null
          : {
              ...hint.topic,
              sources: topicSources.get(hint.topic.id) ?? hint.topic.sources,
            },
      hypothesis_reasons: hint.hypothesis_reasons.map(reason => ({
        ...reason,
        sources: reasonSources.get(reason.id) ?? reason.sources,
      })),
    })),
  };
};

export const mergeInsightSources = (
  payload: InsightPayload,
  axes: InsightAxisSources[],
): InsightPayload => {
  const mergeOne = (axis: InsightAxis): InsightAxis => {
    if (axis.generation_status !== 'processed') return axis;
    const latest = axes.find(candidate => candidate.axis === axis.axis);
    return latest === undefined ? axis : mergeAxis(axis, latest);
  };

  return {
    personal_interest: mergeOne(payload.personal_interest),
    organization_interest: mergeOne(payload.organization_interest),
    outside_interest: mergeOne(payload.outside_interest),
  };
};
